/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                           SECURE LOGIN - FLUTTER-STYLED DESIGN                              ║
 * ║                        Enhanced Authentication with Email Confirmation                       ║
 * ║                        DEPLOYMENT FIX: Privacy Policy Checkbox Issue                        ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useLogging, EventCode } from '../services/logging-service';
import { supabase } from '../lib/supabase';

// UI Components - matching Flutter design
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { ForgotPassword } from './ForgotPassword';
import { PrivacyTerms } from './PrivacyTerms';
import { EmailConfirmation } from './EmailConfirmation';

import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  Shield, 
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader,
  RefreshCw,
  Chrome,
  ArrowRight
} from 'lucide-react';

interface SecureLoginProps {
  onLoginSuccess?: (isNewUser?: boolean) => void;
}

export function SecureLogin({ onLoginSuccess }: SecureLoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setErrorState] = useState(() => {
    // Restore error from localStorage on component mount
    try {
      return localStorage.getItem('clockit-login-error') || '';
    } catch {
      return '';
    }
  });

  // Custom error setter that immediately persists to localStorage
  const setError = (errorMessage: string) => {
    setErrorState(errorMessage);
    
    // Immediately persist to localStorage (synchronous)
    try {
      if (errorMessage) {
        localStorage.setItem('clockit-login-error', errorMessage);
      } else {
        localStorage.removeItem('clockit-login-error');
      }
    } catch (err) {
      console.error('Failed to persist error to localStorage:', err);
    }
  };
  
  // Initialize logging service
  const { logAuthEvent, logPrivacyEvent, logError, startTimer, generateCorrelationId } = useLogging();
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showPrivacyTerms, setShowPrivacyTerms] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [authMethod, setAuthMethod] = useState<'email' | 'oauth' | null>(null);

  const { signIn, signUp, signInWithGoogle, resendConfirmation, isLoading } = useAuth();



  // Password validation
  const validatePassword = (pwd: string) => {
    const errors = [];
    if (pwd.length < 6) errors.push('At least 6 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/\d/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*(),.?\":{}|<>]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  // Handle password change with validation
  const handlePasswordChange = (pwd: string) => {
    setPassword(pwd);
    if (isSignUp) {
      setPasswordErrors(validatePassword(pwd));
    }
  };

  // Convert technical error messages to user-friendly messages
  const getUserFriendlyError = (technicalError: string): string => {
    const errorMessage = technicalError.toLowerCase();
    
    if (errorMessage.includes('email not confirmed')) {
      return 'Please check your email and click the confirmation link to activate your account. Check your spam folder if you don\'t see it.';
    }
    
    if (errorMessage.includes('invalid login credentials') || errorMessage.includes('invalid credentials')) {
      return 'The email or password you entered is incorrect. Please double-check and try again.';
    }
    
    if (errorMessage.includes('already registered') || errorMessage.includes('user already exists')) {
      return 'An account with this email already exists. Try signing in instead, or use the "Forgot password" option.';
    }
    
    if (errorMessage.includes('password') && errorMessage.includes('weak')) {
      return 'Please choose a stronger password that meets our security requirements.';
    }
    
    if (errorMessage.includes('rate limit') || errorMessage.includes('too many')) {
      return 'Too many attempts. Please wait a moment before trying again.';
    }
    
    if (errorMessage.includes('network') || errorMessage.includes('connection')) {
      return 'Connection issue. Please check your internet connection and try again.';
    }
    
    if (errorMessage.includes('password requirements') || errorMessage.includes('password validation')) {
      return 'Your password doesn\'t meet our security requirements. Please check the requirements below.';
    }
    
    if (errorMessage.includes('privacy policy') || errorMessage.includes('terms of service')) {
      return 'Please agree to our Privacy Policy and Terms of Service to continue.';
    }
    
    if (errorMessage.includes('all fields are required') || errorMessage.includes('missing required')) {
      return 'Please fill in all required fields to continue.';
    }
    
    if (errorMessage.includes('passwords do not match')) {
      return 'The passwords you entered don\'t match. Please make sure they\'re identical.';
    }
    
    // Default fallback for unknown errors
    return 'Something went wrong. Please try again, or contact support if the problem persists.';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    setAuthMethod('email'); // Set auth method for email/password

    // Start timing the operation
    const timer = startTimer(`${isSignUp ? 'signup' : 'login'}_operation`);
    const correlationId = generateCorrelationId();

    try {
      if (isSignUp) {
        // Log registration attempt
        await logAuthEvent(EventCode.REGISTRATION_ATTEMPT, undefined, {
          email: email,
          correlation_id: correlationId,
          has_privacy_agreement: privacyAgreed,
        });

        // Validate inputs
        if (!email || !password || !confirmPassword) {
          await logError(EventCode.VALIDATION_ERROR, new Error('Missing required fields'), undefined, {
            missing_fields: [!email ? 'email' : null, !password ? 'password' : null, !confirmPassword ? 'confirmPassword' : null].filter(Boolean),
            operation: 'signup',
            correlation_id: correlationId,
          });
          throw new Error('All fields are required');
        }

        if (password !== confirmPassword) {
          await logError(EventCode.VALIDATION_ERROR, new Error('Password mismatch'), undefined, {
            operation: 'signup',
            error_type: 'password_mismatch',
            correlation_id: correlationId,
          });
          throw new Error('Passwords do not match');
        }

        const passwordValidationErrors = validatePassword(password);
        if (passwordValidationErrors.length > 0) {
          await logError(EventCode.VALIDATION_ERROR, new Error('Password validation failed'), undefined, {
            operation: 'signup',
            validation_errors: passwordValidationErrors,
            correlation_id: correlationId,
          });
          throw new Error(`Password requirements not met`);
        }

        if (!privacyAgreed && (authMethod === null || authMethod === 'email')) {
          await logPrivacyEvent(EventCode.PRIVACY_CONSENT_REQUIRED, undefined, {
            auth_method: 'email',
            consent_required: true,
            correlation_id: correlationId,
          });
          throw new Error('Please agree to the Privacy Policy and Terms of Service to continue');
        }

        // Log privacy acceptance
        if (privacyAgreed) {
          await logPrivacyEvent(EventCode.PRIVACY_POLICY_ACCEPTED, undefined, {
            auth_method: 'email',
            correlation_id: correlationId,
          });
        }

        // Attempt signup
        const result = await signUp(email, password, {
          first_name: email.split('@')[0] // Default first name from email
        });

        if (result.success) {
          await logAuthEvent(EventCode.REGISTRATION_SUCCESS, undefined, {
            email: email,
            correlation_id: correlationId,
          });
          await timer(EventCode.REGISTRATION_SUCCESS, undefined, {
            correlation_id: correlationId,
          });
          
          // Check if user has an active session (immediate sign-in) or needs email verification
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            // Immediate sign-in - user is fully authenticated
            setSuccess('Welcome to Clock-It! Taking you to your dashboard...');
            
            // Clear any persisted error on successful signup
            localStorage.removeItem('clockit-login-error');
            
            onLoginSuccess?.(true); // Pass true for new user
          } else {
            // Email confirmation required
            setSuccess('Welcome to Clock-It! Please check your email to verify your account.');
            
            // Clear any persisted error on successful signup
            localStorage.removeItem('clockit-login-error');
            
            onLoginSuccess?.(true); // Pass true for new user
          }
        } else {
          if (result.error?.includes('already registered')) {
            await logAuthEvent(EventCode.REGISTRATION_FAILED_EMAIL_EXISTS, undefined, {
              email: email,
              correlation_id: correlationId,
            });
          } else {
            await logAuthEvent(EventCode.REGISTRATION_FAILED_VALIDATION, undefined, {
              email: email,
              error: result.error,
              correlation_id: correlationId,
            });
          }
          throw new Error(result.error || 'Signup failed');
        }

      } else {
        // Log login attempt
        await logAuthEvent(EventCode.LOGIN_ATTEMPT, undefined, {
          email: email,
          remember_me: rememberMe,
          correlation_id: correlationId,
        });

        // Validate inputs
        if (!email || !password) {
          await logError(EventCode.VALIDATION_ERROR, new Error('Missing login credentials'), undefined, {
            missing_fields: [!email ? 'email' : null, !password ? 'password' : null].filter(Boolean),
            operation: 'login',
            correlation_id: correlationId,
          });
          throw new Error('Email and password are required');
        }

        // Attempt login
        const result = await signIn(email, password, { rememberMe });

        if (result.success) {
          await logAuthEvent(EventCode.LOGIN_SUCCESS, undefined, {
            email: email,
            remember_me: rememberMe,
            correlation_id: correlationId,
          });
          await timer(EventCode.LOGIN_SUCCESS, undefined, {
            correlation_id: correlationId,
          });
          
          setSuccess('Login successful!');
          // Clear any persisted error on successful login
          localStorage.removeItem('clockit-login-error');
          onLoginSuccess?.(false); // Pass false for returning user login
        } else {
          // Determine specific login failure reason
          if (result.error?.includes('Invalid login credentials')) {
            await logAuthEvent(EventCode.LOGIN_FAILED_INVALID_CREDENTIALS, undefined, {
              email: email,
              correlation_id: correlationId,
            });
          } else if (result.error?.includes('Email not confirmed')) {
            // Email not confirmed - let auth system handle this
            await logAuthEvent(EventCode.LOGIN_FAILED_EMAIL_NOT_VERIFIED, undefined, {
              email: email,
              correlation_id: correlationId,
            });
          } else {
            await logAuthEvent(EventCode.LOGIN_FAILED_INVALID_CREDENTIALS, undefined, {
              email: email,
              error: result.error,
              correlation_id: correlationId,
            });
          }
          throw new Error(result.error || 'Login failed');
        }
      }
    } catch (err: any) {
      console.error('❌ Auth error:', err);
      await logError(EventCode.APPLICATION_ERROR, err, undefined, {
        operation: isSignUp ? 'signup' : 'login',
        email: email,
        correlation_id: correlationId,
      });
      
      // Set user-friendly error message
      const friendlyError = getUserFriendlyError(err.message || 'Authentication failed');
      console.log('🎯 Setting error in state:', friendlyError);
      console.log('🎯 Error state before:', error);
      setError(friendlyError);
      console.log('🎯 Error should now be set...');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleOAuthLogin = async (provider: 'google') => {
    setError('');
    setIsSubmitting(true);
    setAuthMethod('oauth'); // Set auth method for OAuth
    
    const timer = startTimer('oauth_login_operation');
    const correlationId = generateCorrelationId();

    try {
      if (provider === 'google') {
        // Log OAuth initiation
        await logAuthEvent(EventCode.OAUTH_INITIATED, undefined, {
          provider: 'google',
          correlation_id: correlationId,
        });

        // Log that privacy consent is bypassed for OAuth
        await logPrivacyEvent(EventCode.PRIVACY_CONSENT_BYPASSED_OAUTH, undefined, {
          provider: 'google',
          reason: 'oauth_provider_handles_privacy',
          correlation_id: correlationId,
        });

        const result = await signInWithGoogle();
        if (result.success) {
          await logAuthEvent(EventCode.OAUTH_SUCCESS, undefined, {
            provider: 'google',
            correlation_id: correlationId,
          });
          await timer(EventCode.OAUTH_SUCCESS, undefined, {
            correlation_id: correlationId,
          });
          // OAuth redirect will handle the rest
          // The user will be redirected to the callback URL
          // and then routed appropriately (onboarding vs dashboard)
        } else {
          await logAuthEvent(EventCode.OAUTH_FAILED, undefined, {
            provider: 'google',
            error: result.error,
            correlation_id: correlationId,
          });
          setError(result.error || 'OAuth login failed');
        }
      }
    } catch (err: any) {
      await logError(EventCode.API_ERROR, err, undefined, {
        operation: 'oauth_login',
        provider: 'google',
        correlation_id: correlationId,
      });
      const friendlyError = getUserFriendlyError(err.message || 'OAuth login failed');
      setError(friendlyError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!confirmationEmail) return;

    const correlationId = generateCorrelationId();
    setIsSubmitting(true);
    
    try {
      await logAuthEvent(EventCode.PASSWORD_RESET_REQUESTED, undefined, {
        email: confirmationEmail,
        operation: 'resend_confirmation',
        correlation_id: correlationId,
      });

      const result = await resendConfirmation(confirmationEmail);
      if (result.success) {
        await logAuthEvent(EventCode.PASSWORD_RESET_SUCCESS, undefined, {
          email: confirmationEmail,
          operation: 'resend_confirmation',
          correlation_id: correlationId,
        });
        setSuccess('Confirmation email sent!');
      } else {
        await logAuthEvent(EventCode.PASSWORD_RESET_FAILED, undefined, {
          email: confirmationEmail,
          operation: 'resend_confirmation',
          error: result.error,
          correlation_id: correlationId,
        });
        throw new Error(result.error || 'Failed to resend confirmation');
      }
    } catch (err: any) {
      await logError(EventCode.API_ERROR, err, undefined, {
        operation: 'resend_confirmation',
        email: confirmationEmail,
        correlation_id: correlationId,
      });
      const friendlyError = getUserFriendlyError(err.message);
      setError(friendlyError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setSuccess('');
    setPassword('');
    setConfirmPassword('');
    setPasswordErrors([]);
    setShowEmailConfirmation(false);
  };

  const handleForgotPassword = () => {
    console.log('🔍 DEBUG: handleForgotPassword clicked');
    setShowForgotPassword(true);
    console.log('🔍 DEBUG: showForgotPassword set to true');
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
  };

  const handlePasswordResetEmailSent = () => {
    setSuccess('Password reset email sent! Check your inbox.');
    setShowForgotPassword(false);
  };

  const handleShowPrivacyTerms = () => {
    setShowPrivacyTerms(true);
  };

  const handlePrivacyTermsAccept = () => {
    setPrivacyAgreed(true);
    setShowPrivacyTerms(false);
  };

  const handlePrivacyTermsBack = () => {
    setShowPrivacyTerms(false);
  };

  // Show forgot password screen
  if (showForgotPassword) {
    return (
      <ForgotPassword 
        onBack={handleBackToLogin}
        onEmailSent={handlePasswordResetEmailSent}
      />
    );
  }

  // Show privacy terms screen
  if (showPrivacyTerms) {
    return (
      <PrivacyTerms
        onAccept={handlePrivacyTermsAccept}
        onBack={handlePrivacyTermsBack}
      />
    );
  }

  // Show email confirmation screen
  if (showEmailConfirmation) {
    return (
      <EmailConfirmation
        email={confirmationEmail}
        onBack={() => setShowEmailConfirmation(false)}
        onConfirmed={() => {
          console.log('✅ Email confirmed, proceeding to app');
          // Store verified user state (will be cleaned up by auth state change)
          localStorage.setItem('clockit-pending-user', JSON.stringify({
            email: confirmationEmail,
            needsVerification: false, // Just verified!
            timestamp: Date.now()
          }));
          setShowEmailConfirmation(false);
          onLoginSuccess?.(false); // OAuth users - assume returning user for now
        }}
        onContinueToApp={() => {
          console.log('📱 User chose to continue without verification');
          // Store pending user state for app access
          localStorage.setItem('clockit-pending-user', JSON.stringify({
            email: confirmationEmail,
            needsVerification: true,
            timestamp: Date.now()
          }));
          setShowEmailConfirmation(false);
          onLoginSuccess?.(false); // User completing verification - returning user
        }}
        isNewUser={true}
      />
    );
  }

  // Main login/signup form
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* App Logo and Title */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
              <Clock className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold">Clock-It</h1>
          <p className="text-muted-foreground">
            Worker-first time tracking & rights protection
          </p>
        </div>

        {/* Login Card */}
        <Card className="clock-it-card">
          <CardHeader className="text-center">
            <CardTitle>
              {isSignUp ? 'Create Account' : 'Welcome Back'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {isSignUp 
                ? 'Join thousands of workers protecting their rights'
                : 'Sign in to your Clock-It account'
              }
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  {error}
                  {error.toLowerCase().includes('confirmation') && !isSignUp && (
                    <div className="mt-3">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          if (email) {
                            setConfirmationEmail(email);
                            handleResendConfirmation();
                          }
                        }}
                        disabled={isSubmitting}
                        className="w-full"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin mr-2" />
                            Sending...
                          </>
                        ) : (
                          'Resend Confirmation Email'
                        )}
                      </Button>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Success Alert */}
            {success && (
              <Alert>
                <CheckCircle className="w-4 h-4" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            {/* OAuth Buttons */}
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={() => handleOAuthLogin('google')}
                disabled={isLoading || isSubmitting}
              >
                <Chrome className="w-5 h-5 mr-3" />
                Continue with Google
              </Button>
            </div>

            <div className="relative">
              <Separator />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="bg-background px-2 text-xs text-muted-foreground">
                  OR
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    disabled={isLoading || isSubmitting}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    className={`pl-10 pr-10 ${isSignUp && passwordErrors.length > 0 ? 'border-red-500' : ''}`}
                    required
                    disabled={isLoading || isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {isSignUp && passwordErrors.length > 0 && (
                  <div className="text-xs text-red-500 space-y-1">
                    <p className="font-medium">Password must contain:</p>
                    {passwordErrors.map((error, index) => (
                      <p key={index} className="flex items-center text-xs">
                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirm Password Field (Sign Up Only) */}
              <AnimatePresence>
                {isSignUp && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-2"
                  >
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`pl-10 pr-10 ${isSignUp && confirmPassword && password !== confirmPassword ? 'border-red-500' : ''}`}
                        required={isSignUp}
                        disabled={isLoading || isSubmitting}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-xs text-red-500">Passwords do not match</p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Remember Me - Login Only */}
              <AnimatePresence>
                {!isSignUp && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id="rememberMe"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(!!checked)}
                    />
                    <Label 
                      htmlFor="rememberMe" 
                      className="text-sm font-normal cursor-pointer"
                    >
                      Remember me for 30 days
                    </Label>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Privacy Agreement - Email/Password Signup Only */}
              <AnimatePresence>
                {isSignUp && (authMethod === null || authMethod === 'email') && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-3"
                  >
                    {/* Debug Info - Remove in production */}
                    {process.env.NODE_ENV === 'development' && (
                      <div className="text-xs bg-yellow-100 p-2 rounded">
                        Debug: isSignUp={String(isSignUp)}, authMethod={String(authMethod)}, privacyAgreed={String(privacyAgreed)}
                      </div>
                    )}
                    
                    {/* Privacy Agreement Card */}
                    <div className="bg-blue-50/50 dark:bg-blue-950/20 rounded-lg border border-blue-100 dark:border-blue-900/30 p-4 space-y-3">
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="privacyAgreement"
                          checked={privacyAgreed}
                          onCheckedChange={(checked) => setPrivacyAgreed(!!checked)}
                          className="mt-1 flex-shrink-0"
                        />
                        <div className="flex-1 space-y-2">
                          <Label 
                            htmlFor="privacyAgreement" 
                            className="text-sm font-medium text-foreground leading-relaxed cursor-pointer"
                          >
                            I agree to Clock-It's data handling practices
                          </Label>
                          
                          {/* Compact Policy Links */}
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="text-muted-foreground">Read our</span>
                            <button
                              type="button"
                              onClick={handleShowPrivacyTerms}
                              className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 font-medium transition-colors"
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              Privacy Policy
                            </button>
                            <span className="text-muted-foreground">&</span>
                            <button
                              type="button"
                              onClick={handleShowPrivacyTerms}
                              className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 font-medium transition-colors"
                            >
                              <Lock className="w-3 h-3 mr-1" />
                              Terms
                            </button>
                          </div>

                        </div>
                      </div>
                      
                      {/* Required Notice */}
                      {!privacyAgreed && (
                        <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded px-2 py-1 border border-amber-200 dark:border-amber-900/50">
                          Agreement required to create your account
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12"
                disabled={
                  isLoading || 
                  isSubmitting ||
                  !email || 
                  !password || 
                  (isSignUp && (passwordErrors.length > 0 || !confirmPassword || password !== confirmPassword || ((authMethod === null || authMethod === 'email') && !privacyAgreed)))
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    {isSignUp ? 'Creating Account...' : 'Signing In...'}
                  </>
                ) : (
                  <>
                    {isSignUp ? 'Create Account' : 'Sign In'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              {/* Toggle Mode */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={toggleMode}
                  className="text-sm text-primary hover:underline"
                >
                  {isSignUp 
                    ? 'Already have an account? Sign in' 
                    : "Don't have an account? Sign up"
                  }
                </button>
              </div>
            </form>

            {/* Forgot Password (only show for sign in) */}
            {!isSignUp && (
              <div className="text-center">
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('🔍 DEBUG: Forgot password button clicked');
                    handleForgotPassword();
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trust Indicators */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-4 text-xs">
            <div className="flex items-center space-x-1">
              <Shield className="w-3 h-3 text-green-600 dark:text-green-400" />
              <span className="text-muted-foreground">Privacy First</span>
            </div>
            <div className="flex items-center space-x-1">
              <Lock className="w-3 h-3 text-blue-600 dark:text-blue-400" />
              <span className="text-muted-foreground">Encrypted</span>
            </div>
            <div className="flex items-center space-x-1">
              <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400" />
              <span className="text-muted-foreground">No Tracking</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Your data is encrypted and never shared without consent
          </p>
        </div>
      </div>
    </div>
  );
}